import {
  CheckCircle,
  Close,
  Receipt
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useDataGrid } from '@refinedev/mui';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getMonths, processMultipleTransactions, searchCreditsByClient } from '../../../services/credits_v2';
import { ClientCredits, FindDayProps, GroupedTransactions, ProcessTransactionPayload, StatCardProps, Transaction } from './models';

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, title, value }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Icon color="primary" />
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Stack>
      <Typography variant="h5" component="div" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export const FindDay: React.FC<FindDayProps> = ({
  open,
  handleClose,
  date,
  onSetDate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { tableQueryResult: { refetch } } = useDataGrid();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState<string | null>(null);
  const [clientNameFilter, setClientNameFilter] = useState<string>("");
  const [clientCredits, setClientCredits] = useState<ClientCredits | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<Transaction[]>([]);

  const { data: months } = useQuery("months", getMonths);

  const monthOptions: string[] = months?.data?.map((item: { month: string }) => item.month) || [];

  const handleSearch = async (client_name: string, month: string): Promise<void> => {
    if (!selectedMonth || !client_name) {
      setError('Please select both a month and a client');
      return;
    }

    try {
      setIsLoading(true);
      const credits = await searchCreditsByClient(client_name, month);
      setClientCredits(credits?.data);
      setError(null);
    } catch (error) {
      setError('Failed to fetch client credits');
      setClientCredits(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMonth && clientSearch) {
      handleSearch(clientSearch, selectedMonth);
    }
  }, [selectedMonth, clientSearch]);

  const groupedTransactions = useMemo<Record<number, GroupedTransactions>>(() => {
    if (!clientCredits) return {};
    return clientCredits.transactions.reduce((acc: Record<number, GroupedTransactions>, transaction: Transaction) => {
      const clientId = transaction.client.id;
      if (!acc[clientId]) {
        acc[clientId] = {
          client: transaction.client,
          id: clientId,
          transactions: []
        };
      }
      acc[clientId].transactions.push(transaction);
      return acc;
    }, {});
  }, [clientCredits]);

  // Count of all clients and transactions
  const transactionCounts = useMemo(() => {
    const clientCount = Object.keys(groupedTransactions).length;
    const transactionCount = Object.values(groupedTransactions)
      .reduce((count, group) => count + group.transactions.length, 0);

    return { clientCount, transactionCount };
  }, [groupedTransactions]);

  console.log({
    groupedTransactions: Object.values(groupedTransactions),
    transactionCounts
  });


  const handleTransactionSelect = (transaction: Transaction): void => {
    setSelectedTransactions(prev => {
      const isSelected = prev.some(t => t.id === transaction.id);
      return isSelected ? prev.filter(t => t.id !== transaction.id) : [...prev, transaction];
    });
  };

  const handleProcessTransactions = async (): Promise<void> => {
    try {
      const payload: ProcessTransactionPayload[] = selectedTransactions.map(transaction => ({
        id: transaction.id,
        amount: transaction.amount,
        credit_v2_id: transaction.client.id,
        client: {
          id: transaction.client.id,
          name: transaction.client.name
        }
      })) as any;

      await processMultipleTransactions(payload);

      // Invalidate and refetch relevant queries
      refetch();

      setSelectedTransactions([]);
      handleClose();
    } catch (error) {
      console.error('Failed to process transactions:', error);
      setError('Failed to process transactions. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{

      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Client Credits Search</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Search Controls */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Autocomplete<string, false>
              options={monthOptions}
              value={selectedMonth}
              onChange={(_, newValue) => setSelectedMonth(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Month"
                  variant="outlined"
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                />
              )}
              sx={{ flex: 1 }}
            />

            <Autocomplete
              options={Object.values(groupedTransactions).flatMap(group =>
                group.transactions
              )}
              value={null}
              onChange={(_, newValue) => newValue && handleTransactionSelect(newValue)}
              groupBy={(option) => `${option.client.name} ${option.client.pseudo ? `(${option.client.pseudo})` : ''}`}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Typography color="black" sx={{ fontWeight: 'bold', p: 1 }}>
                    {params.group}
                  </Typography>
                  {params.children}
                </li>
              )}
              getOptionLabel={(option) => `${option.client.name} ${option.client.pseudo ? `(${option.client.pseudo})` : ''}`}
              filterOptions={(options, state) => {
                if (!state.inputValue) return options;

                return options.filter(option => {
                  const searchText = state.inputValue.toLowerCase();
                  const nameMatch = option.client.name.toLowerCase().includes(searchText);
                  const pseudoMatch = option.client.pseudo ?
                    option.client.pseudo.toLowerCase().includes(searchText) : false;

                  return nameMatch || pseudoMatch;
                });
              }}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Unpaid Transactions"
                  variant="outlined"
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  // Don't use onChange here - Autocomplete handles the filtering internally
                  onChange={(e) => {
                    // Only update client search for API calls
                    setClientSearch(e.target.value);
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params?.inputProps?.value && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Clear the input field and force refresh
                              const event = { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>;
                              if (params?.inputProps?.onChange) {
                                params.inputProps.onChange(event);
                              }
                              setClientSearch(null);
                            }}
                            edge="end"
                            sx={{ zIndex: 10 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                clientNameFilter ?
                  `No clients found matching "${clientNameFilter}"` :
                  "Select a month and enter client name to search"
              }
              renderOption={(props, option) => {
                const isSelected = selectedTransactions.some(t => t.id === option.id);
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant={isMobile ? "body2" : "body1"}>{option.amount} <sup>TND</sup></Typography>
                        <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary">{option.day}</Typography>
                      </Box>
                      <Chip
                        label={isSelected ? 'Selected' : 'Select'}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </Box>
                  </li>
                );
              }}
              sx={{ flex: 1 }}
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}

          {isLoading && (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}

          {clientCredits && (
            <>

              {/* Transactions Section */}
              <Box
                sx={{
                  height: { xs: '350px', sm: '400px' },
                  overflow: 'hidden'
                }}
              >
                {/* Selected Transactions */}
                <Box sx={{ height: '100%', overflow: 'auto', pr: { xs: 0, sm: 2 }, pb: 2 }}>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Receipt color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Selected Transactions ({selectedTransactions.length})
                    </Typography>
                  </Box>

                  {selectedTransactions.length === 0 ? (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '80%',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2
                    }}>
                      <Typography color="text.secondary" align="center">
                        No transactions selected. Search and select transactions to process.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {selectedTransactions.map((transaction) => (
                        <Card
                          key={transaction.id}
                          variant="outlined"
                          sx={{
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: theme.shadows[2],
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            <Stack spacing={1}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 'bold',
                                      fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                  >
                                    {transaction.client.name}
                                    {transaction.client.pseudo && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          ml: 1,
                                          color: 'text.secondary',
                                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}
                                      >
                                        ({transaction.client.pseudo})
                                      </Typography>
                                    )}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => handleTransactionSelect(transaction)}
                                  sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': { bgcolor: theme.palette.error.light + '20' }
                                  }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Box>
                              <Divider />
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography
                                    color="text.secondary"
                                    variant="body2"
                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                  >
                                    Amount
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 'medium',
                                      color: theme.palette.success.main,
                                      fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                  >
                                    {transaction.amount.toLocaleString()} TND
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    color="text.secondary"
                                    variant="body2"
                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                  >
                                    Date
                                  </Typography>
                                  <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    {transaction.day}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 2 }}>
        <Button
          onClick={handleClose}
          size={isMobile ? "small" : "medium"}
        >
          Cancel
        </Button>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end ",
          gap: 1
        }}>
          <Button
            onClick={handleProcessTransactions}
            variant="contained"
            color="primary"
            disabled={selectedTransactions.length === 0}
            size={isMobile ? "small" : "medium"}
            startIcon={<CheckCircle />}
          >
            Process {selectedTransactions.length} Transaction{selectedTransactions.length !== 1 ? 's' : ''}
          </Button>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 700 }}>TOTAL TO PROCESS</Typography>
          <Typography variant="body1" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, fontWeight: 500 }}>{selectedTransactions?.reduce((total, transaction) => total + transaction?.amount, 0)?.toLocaleString()} TND</Typography>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FindDay;